import {
  toCents,
  toDecimal,
  type PriceList,
  type Product,
} from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import { insertRow } from "../../lib/queries";
import { supabase, unwrap } from "../../lib/supabase";

/**
 * What each supplier charges for the same product.
 *
 * The catalogue holds one name for a thing, which is right — a 13" ridge cap is
 * a 13" ridge cap wherever it is bought. The price is not one number, and until
 * this screen existed it pretended to be: whichever price sheet was loaded last
 * won, silently, for every supplier.
 *
 * A blank price is not zero. It means this supplier has not quoted this product
 * and the catalogue cost is used, which is what the row says in grey.
 */
export function PricingTool() {
  const [listId, setListId] = useState("");
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(60);
  const [error, setError] = useState<string | null>(null);

  const lists = useAsync(
    async () =>
      unwrap<PriceList[]>(
        await supabase.from("price_lists").select("*").order("name"),
      ),
    [],
  );

  const chosen = listId || lists.data?.[0]?.id || "";

  const catalogue = useAsync(async () => {
    const [products, prices, suppliers] = await Promise.all([
      supabase
        .from("products")
        .select("id,name,category,unit,default_cost")
        .eq("active", true)
        .order("name"),
      supabase.from("product_prices").select("product_id,cost"),
      supabase
        .from("suppliers")
        .select("name,price_list_id")
        .eq("active", true),
    ]);
    return { products, prices, suppliers };
  }, []);

  const priced = useAsync(async () => {
    if (!chosen) return [] as { product_id: string; cost: number }[];
    return unwrap<{ product_id: string; cost: number }[]>(
      await supabase
        .from("product_prices")
        .select("product_id,cost")
        .eq("price_list_id", chosen),
    );
  }, [chosen]);

  const products = unwrap<Product[]>(
    catalogue.data?.products ?? { data: [], error: null },
  );
  const byProduct = new Map(
    (priced.data ?? []).map((row) => [row.product_id, Number(row.cost)]),
  );

  const usedBy = unwrap<{ name: string; price_list_id: string | null }[]>(
    catalogue.data?.suppliers ?? { data: [], error: null },
  )
    .filter((s) => s.price_list_id === chosen)
    .map((s) => s.name);

  const filtered = search
    ? products.filter((p) =>
        `${p.name} ${p.category ?? ""}`
          .toLowerCase()
          .includes(search.toLowerCase()),
      )
    : products;
  const shown = filtered.slice(0, limit);

  const guard = async (action: () => Promise<void>) => {
    try {
      await action();
      setError(null);
      await priced.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const save = (productId: string, raw: string | null) =>
    void guard(async () => {
      if (raw === null || raw.trim() === "") {
        // Clearing the box means "this supplier does not quote this", which is
        // a different statement from "it is free". The row goes away.
        await supabase
          .from("product_prices")
          .delete()
          .eq("price_list_id", chosen)
          .eq("product_id", productId);
        return;
      }
      const parsed = Number(raw);
      if (!Number.isFinite(parsed) || parsed < 0)
        throw new Error(t('"{value}" is not a price.', { value: raw }));
      const cost = toDecimal(toCents(parsed));
      await supabase
        .from("product_prices")
        .upsert(
          {
            price_list_id: chosen,
            product_id: productId,
            cost,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "price_list_id,product_id" },
        )
        .throwOnError();
    });

  return (
    <>
      <ErrorNote error={error} />
      <div className="toolbar2">
        <select
          value={chosen}
          onChange={(e) => {
            setListId(e.target.value);
            setLimit(60);
          }}
        >
          {(lists.data ?? []).map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
        <input
          placeholder={t("Search…")}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setLimit(60);
          }}
        />
        <span className="count">
          {t("{priced} priced · {n} products", {
            priced: byProduct.size,
            n: filtered.length,
          })}
        </span>
        <button
          className="addrow"
          style={{ marginTop: 0 }}
          onClick={() => {
            const name = window.prompt(t("Name the new price sheet"));
            if (!name?.trim()) return;
            void guard(async () => {
              const row = await insertRow<{ id: string }>("price_lists", {
                name: name.trim(),
              });
              setListId(row.id);
              await lists.reload();
            });
          }}
        >
          {t("+ New price sheet")}
        </button>
      </div>

      <div className="na-hint" style={{ marginBottom: 10 }}>
        {usedBy.length > 0
          ? t("Used by {names}.", { names: usedBy.join(", ") })
          : t(
              "No supplier reads off this sheet yet — set one under Tools → Suppliers.",
            )}{" "}
        {t(
          "A blank price means this supplier has not quoted the product, and the catalogue cost is used instead.",
        )}
      </div>

      <div className="ewrap">
        <table className="etable">
          <thead>
            <tr>
              <th>{t("Product")}</th>
              <th>{t("Category")}</th>
              <th>{t("Unit")}</th>
              <th className="num">{t("Catalogue cost")}</th>
              <th className="num">{t("This supplier")}</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr>
                <td colSpan={5} className="tnone">
                  {t("No products.")}
                </td>
              </tr>
            ) : (
              shown.map((product) => {
                const own = byProduct.get(product.id);
                return (
                  <tr key={product.id}>
                    <td>
                      <span className="lockedval" style={{ minWidth: 240 }}>
                        {product.name}
                      </span>
                    </td>
                    <td>{product.category}</td>
                    <td>{product.unit}</td>
                    <td
                      className="num"
                      style={{ opacity: own == null ? 1 : 0.5 }}
                    >
                      {money(product.default_cost)}
                    </td>
                    <td className="num">
                      <PriceInput
                        value={own}
                        placeholder={money(product.default_cost)}
                        onSave={(v) => save(product.id, v)}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {filtered.length > shown.length && (
        <button
          className="addrow ghost"
          onClick={() => setLimit((l) => l + 60)}
        >
          {t("Show more")}
        </button>
      )}
    </>
  );
}

const money = (value: unknown): string => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : "—";
};

function PriceInput({
  value,
  placeholder,
  onSave,
}: {
  value: number | undefined;
  placeholder: string;
  onSave: (value: string | null) => void;
}) {
  const initial = value == null ? "" : String(value);
  const [local, setLocal] = useState(initial);
  const [dirty, setDirty] = useState(false);
  if (!dirty && initial !== local) setLocal(initial);

  return (
    <input
      className="num"
      value={local}
      placeholder={placeholder}
      style={{ minWidth: 90, textAlign: "right" }}
      onChange={(e) => {
        setLocal(e.target.value);
        setDirty(true);
      }}
      onBlur={() => {
        if (!dirty) return;
        setDirty(false);
        onSave(local);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
    />
  );
}
